import React, { useState } from 'react';
import { useReportApi } from '../../../../hooks/useReportApi';

const REPORT_TYPES = [
  { value: 'business', label: '업무 보고서' },
  { value: 'project', label: '프로젝트 보고서' },
  { value: 'analysis', label: '분석 보고서' },
  { value: 'proposal', label: '제안서' }
];

const TYPE_EXAMPLES = {
  business: {
    topic: '2024년 1분기 영업실적 보고',
    context: '영업팀의 1분기 실적과 주요 성과, 향후 계획을 정리한 업무 보고서입니다.',
    keyPoints: ['매출 목표 달성률', '신규 고객 유치 현황', '주요 성공 사례']
  },
  project: {
    topic: '신규 웹사이트 개발 프로젝트 현황',
    context: '진행 중인 웹사이트 리뉴얼 프로젝트의 진행 상황과 이슈를 정리합니다.',
    keyPoints: ['개발 진행 현황', '주요 기술적 이슈', '일정 및 리소스 현황']
  },
  analysis: {
    topic: '경쟁사 서비스 분석 리포트',
    context: '주요 경쟁사들의 서비스 특징과 시장 포지셔닝을 분석합니다.',
    keyPoints: ['시장 점유율 분석', '주요 서비스 비교', '차별화 전략']
  },
  proposal: {
    topic: '업무 프로세스 개선 제안',
    context: '현재 업무 프로세스의 비효율성을 분석하고 개선 방안을 제시합니다.',
    keyPoints: ['현재 문제점', '개선 방안', '기대 효과']
  }
};

export default function ReportWriter({ onResult = () => {} }) {
  const { generateReport, loading, error } = useReportApi();
  const [formData, setFormData] = useState({
    type: 'business',
    topic: '',
    context: '',
    keyPoints: [''],
    requirements: {}
  });
  const [result, setResult] = useState(null);

  // 현재 선택된 타입의 예시 가져오기
  const currentExample = TYPE_EXAMPLES[formData.type];

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log('Submitting report data:', formData);
      const generatedResult = await generateReport(formData);
      console.log('Report generated:', generatedResult);
      setResult(generatedResult);
      onResult(generatedResult);
    } catch (error) {
      console.error('보고서 생성 오류:', {
        message: error.message,
        formData
      });
    }
  };

  const handleKeyPointChange = (index, value) => {
    const newKeyPoints = [...formData.keyPoints];
    newKeyPoints[index] = value;
    setFormData({ ...formData, keyPoints: newKeyPoints });
  };

  const addKeyPoint = () => {
    setFormData({
      ...formData,
      keyPoints: [...formData.keyPoints, '']
    });
  };

  const removeKeyPoint = (index) => {
    const newKeyPoints = formData.keyPoints.filter((_, i) => i !== index);
    setFormData({ ...formData, keyPoints: newKeyPoints });
  };

  return (
    <div className="max-w-2xl mx-auto p-4">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            보고서 유형
          </label>
          <select
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          >
            {REPORT_TYPES.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            주제
          </label>
          <input
            type="text"
            value={formData.topic}
            onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
            placeholder={currentExample.topic}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            required
          />
          <p className="mt-1 text-sm text-gray-500">
            예시: {currentExample.topic}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            컨텍스트
          </label>
          <textarea
            value={formData.context}
            onChange={(e) => setFormData({ ...formData, context: e.target.value })}
            placeholder={currentExample.context}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            rows={3}
            required
          />
          <p className="mt-1 text-sm text-gray-500">
            예시: {currentExample.context}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            핵심 포인트
          </label>
          {formData.keyPoints.map((point, index) => (
            <div key={index} className="flex gap-2 mt-2">
              <input
                type="text"
                value={point}
                onChange={(e) => handleKeyPointChange(index, e.target.value)}
                placeholder={currentExample.keyPoints[index] || '새로운 핵심 포인트'}
                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
              {formData.keyPoints.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeKeyPoint(index)}
                  className="px-2 py-1 text-red-600 hover:text-red-800"
                >
                  삭제
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addKeyPoint}
            className="mt-2 text-sm text-indigo-600 hover:text-indigo-800"
          >
            + 핵심 포인트 추가
          </button>
          <p className="mt-1 text-sm text-gray-500">
            예시 핵심 포인트: {currentExample.keyPoints.join(', ')}
          </p>
        </div>

        {error && (
          <div className="text-red-600 text-sm">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-400"
        >
          {loading ? '생성 중...' : '보고서 생성'}
        </button>
      </form>
      
      {result && <ReportResult result={result} />}
    </div>
  );
} 